import Logger from './logger';

/**
 * AI Scanner session persistence (#724). A "dumb store" for the `ChatSession`
 * in `localStorage`: it serializes what it's given and reads it back — the
 * merge/append logic lives in the `persistAiScannerRound` thunk. Storage access
 * is wrapped so a private-mode / quota / disabled failure no-ops and logs
 * `E0048`. See `specs/i724/`.
 */

/** The single versioned key holding the whole `ChatSession`. */
export const STORAGE_KEY = 'aiScanner.session.v1';

/** Payload shape version; a mismatch reads as "nothing saved". */
export const SCHEMA_VERSION = 1;

/**
 * Persist the `ChatSession`, overwriting the previous value. No-op when the
 * write throws. Feature-flag gating is the view's responsibility — the scanner
 * UI does not render when the flag is off, so this is never reached then.
 *
 * @param {object} session - The full `ChatSession` to persist.
 */
export function save(session) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    Logger.error({
      errorCode: 'E0048',
      message: 'aiScannerSession: failed to save session',
      error
    });
  }
}

/**
 * Read the saved `ChatSession`, or `null` when there's nothing usable to
 * restore (missing key, bad JSON, wrong shape, or schema mismatch — all
 * expected, not logged). A storage-access failure returns `null` and logs
 * `E0048`. Flag-agnostic: nothing is written while the flag is off, so a read
 * just finds no data.
 *
 * @returns {object|null} The parsed `ChatSession`, or `null`.
 */
export function load() {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    Logger.error({
      errorCode: 'E0048',
      message: 'aiScannerSession: failed to load session',
      error
    });
    return null;
  }

  if (!raw) return null;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Corrupt value — expected "nothing to restore", not an error.
    return null;
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schemaVersion !== SCHEMA_VERSION ||
    typeof parsed.sessionId !== 'string' ||
    !Array.isArray(parsed.searchRounds)
  ) {
    return null;
  }

  return parsed;
}

/** Remove the persisted session. No-op on failure (logged with `E0048`). */
export function clear() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    Logger.error({
      errorCode: 'E0048',
      message: 'aiScannerSession: failed to clear session',
      error
    });
  }
}
